var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useRef, useState, useEffect, useMemo } from 'react';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { GRID_SIZE, JUMP_BAR_SNAP_THRESHOLD, JUMP_BAR_EDGE_SNAP_THRESHOLD, JUMP_BAR_TICK_SNAP_THRESHOLD, } from '../constants/numberLineConfig';
import { computeTickPositions, computePxPerUnit, findParentLineGeometry, collectJumpBarEdges, snapToJumpBarEdge, parseValueToNumber, formatJumpBarLength, jumpBarCandidateDenominators, numberLineLabelToPlainText, gcd, decimalToFraction, JUMP_BAR_EXACT_FRACTION_MAX_DENOM, } from '../helper/numberLineHelper';
import NumberLineLabel from './NumberLineLabel';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { bringManipulativesToFront } from '../helper/helper';
import MathEditor, { parseNumericLatex } from '../components/MathEditor';
import ButtonComponent from '../components/ButtonComponent';
import '../styles/widgets/numberLineJumpBar.scss';
import { t } from 'i18next';
import customEventHandler from '../helper/customEvents';
import { useDuplicateAnnounce, useCreationAnnounce, } from '../helper/hooks/useDuplicateAnnounce';
var JUMP_BAR_BAR_HEIGHT = 30; // bar-only portion; center.y always = bar top (arc is absolute)
var NumberLineJumpBar = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
    var data = _a.data;
    var _v = useContext(DataContext), manipulative = _v.manipulative, setManipulative = _v.setManipulative, selectedTool = _v.selectedTool, setSelectedTool = _v.setSelectedTool, setContextMenu = _v.setContextMenu;
    var _w = globalStore.useStoreState(function (store) { return store.playerStore; }), zoomLevel = _w.zoomLevel, playerBgPattern = _w.playerBgPattern;
    var _x = globalStore.useStoreState(function (s) { return s.jsonDataStore; }), bottomToolConfig = _x.bottomToolConfig, btnConfig = _x.btnConfig;
    // Unified label → number parser. Used everywhere we resolve the JB's
    // typed/displayed label back to a numeric length:
    //   - parseNumericLatex matches LaTeX fragments produced by the math
    //     editor's fraction button: "\frac{1}{2}", "1\frac{1}{2}", and bare
    //     integers ("3"). Returns isValid=false for everything else.
    //   - parseValueToNumber covers the plain-text path: integers, decimals
    //     ("0.5"), simple fractions ("1/2"), and mixed numbers ("1 1/2").
    // Fall through from latex to plain so a user typing "1/2" as text still
    // resizes, AND inserting it as a LaTeX fraction via the math button does too.
    var parseLabelToNumber = useCallback(function (label) {
        var trimmed = label.trim();
        if (trimmed === '')
            return 0;
        var latex = parseNumericLatex(trimmed);
        if (latex.isValid && latex.value !== null) {
            return typeof latex.value === 'number'
                ? latex.value
                : latex.value.deno !== 0
                    ? latex.value.num / latex.value.deno
                    : 0;
        }
        return parseValueToNumber(trimmed);
    }, []);
    var id = data.id, numberLineId = data.numberLineId, center = data.center, width = data.width, direction = data.direction, text = data.text, unitLength = data.unitLength, showNumericalValue = data.showNumericalValue, jumpBarScale = data.scale, jumpBarLock = data.lock;
    // Look up parent number line
    var parentLine = useMemo(function () {
        if (!numberLineId)
            return null;
        var parent = manipulative.find(function (m) { return m.data.id === numberLineId; });
        if (!parent || parent.data.type !== 'numberLine')
            return null;
        return parent.data;
    }, [numberLineId, manipulative]);
    // Derived values from parent
    // pxPerUnit is UNSCALED — used for local width/unitLength calculations
    var pxPerUnit = parentLine
        ? computePxPerUnit(parentLine.gridSquaresPerUnit, parentLine.intervalNum, !!parentLine.scalingToManipulatives)
        : GRID_SIZE;
    var parentScale = (parentLine === null || parentLine === void 0 ? void 0 : parentLine.scale) || 1;
    // Grid-candidate math (see the whole-scale comments in `snapXToGrid` and
    // the drag-move handler below) only stays aligned to the tick lattice at
    // scale 1 — at any other scale, even a "whole" one, the raw GRID_SIZE step
    // no longer reliably lines up with rendered gridlines. Restrict the
    // teacher-facing toggle's actual effect to scale 1; the popup already
    // warns that the option only applies there.
    var isScaleOne = Math.abs(parentScale - 1) < 1e-6;
    var jumpBarSnapToGrid = ((_b = parentLine === null || parentLine === void 0 ? void 0 : parentLine.jumpBarSnapToGrid) !== null && _b !== void 0 ? _b : false) && isScaleOne;
    var isLengthLocked = (_c = jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.length) !== null && _c !== void 0 ? _c : false;
    // Ticks in world-space (scaled) for snap functions
    var ticks = useMemo(function () {
        if (!parentLine || parentLine.lineMode === 'noTickMarks')
            return [];
        return computeTickPositions(parentLine.startNum, parentLine.endNum, parentLine.intervalNum, pxPerUnit * parentScale);
    }, [parentLine, pxPerUnit, parentScale]);
    // Sibling jump bars (for edge snapping) — must exclude other manipulative
    // types (e.g. customTick) that share the same numberLineId, since only
    // jumpBar has the `width` field edge-snapping relies on.
    var siblingBars = useMemo(function () {
        return manipulative
            .filter(function (m) {
            return m.data.type === 'jumpBar' &&
                m.data.numberLineId === numberLineId &&
                m.data.id !== id;
        })
            .map(function (m) { return m.data; });
    }, [manipulative, numberLineId, id]);
    // Custom tick marks linked to the same number line — treated as tick-type
    // snap candidates (not edge/sibling candidates, since they lack `width`).
    // Position → line-local px mirrors NumberLineCustomTick.tsx's own
    // world-position calculation so the values are directly comparable to
    // `ticks`/`tickCandidates` (both computed with pxPerUnit * parentScale).
    var customTickCandidates = useMemo(function () {
        if (!parentLine)
            return [];
        return manipulative
            .filter(function (m) {
            return m.data.type === 'customTick' &&
                m.data.numberLineId === numberLineId;
        })
            .map(function (m) {
            var ct = m.data;
            return (ct.position - parentLine.startNum) * pxPerUnit * parentScale;
        });
    }, [manipulative, numberLineId, parentLine, pxPerUnit, parentScale]);
    // Selection
    var isFocused = selectedTool.includes(id);
    // --- A11Y: outer wrapper ref + lifecycle aria-label overrides ---
    var outerRef = useRef(null);
    var barBodyRef = useRef(null);
    var jbName = t('text.jumpBarRoleDescription');
    var duplicateLabel = useDuplicateAnnounce(id, jbName);
    var creationLabel = useCreationAnnounce(id, jbName);
    var _y = useState({ x: center.x, y: center.y }), barPosition = _y[0], setBarPosition = _y[1];
    // Transient CSS-only bring-to-front boost while this bar (or its parent
    // line, dragging the whole family) is actively being dragged.
    var _z = useState(false), isDragging = _z[0], setIsDragging = _z[1];
    useEffect(function () {
        var onDragActive = function (ids) { return setIsDragging(ids.includes(id)); };
        customEventHandler.addEventListener('numberLineDragActive', onDragActive);
        return function () {
            customEventHandler.removeEventListener('numberLineDragActive', onDragActive);
        };
    }, [id]);
    var _0 = useState(width), barWidth = _0[0], setBarWidth = _0[1];
    var _1 = useState(direction), barDirection = _1[0], setBarDirection = _1[1];
    var _2 = useState(false), isLengthEditing = _2[0], setIsLengthEditing = _2[1];
    var _3 = useState(false), isEditorFocused = _3[0], setIsEditorFocused = _3[1];
    var _4 = useState(-1), activeMathInput = _4[0], setActiveMathInput = _4[1];
    var _5 = useState(((_d = data.text) === null || _d === void 0 ? void 0 : _d.length) ? data.text : [{ type: 'text', value: '' }]), localText = _5[0], setLocalText = _5[1];
    var localTextRef = useRef(localText);
    var hasContent = localText.some(function (item) {
        var _a, _b, _c;
        return (item.type === 'text' && ((_a = item.value) === null || _a === void 0 ? void 0 : _a.trim())) ||
            (item.type === 'math' && (((_b = item.latex) === null || _b === void 0 ? void 0 : _b.trim()) || ((_c = item.value) === null || _c === void 0 ? void 0 : _c.trim())));
    });
    var mathEditorRef = useRef(null);
    var labelContainerRef = useRef(null);
    var dataBeforeEditRef = useRef(null);
    // Touch tap-vs-drag tracking for the JB label (issue #4). On iPad, an
    // onMouseDown that immediately enters edit mode causes the keyboard to
    // open before iOS can recognize a drag, so a single finger-down to drag
    // the bar by its label opens the editor instead. We defer entering edit
    // mode on touch until touchend confirms the gesture was a tap (small
    // movement + short duration). The mouse path stays instant for desktop.
    var labelTouchStartRef = useRef(null);
    var labelLastTouchTimeRef = useRef(0);
    // Keyboard activation handlers now live directly on the two
    // semantic tab stops (.jumpBarBar opens the context menu;
    // .jumpBarLabelContent enters edit mode), so no wrapper-level
    // keydown handler is needed.
    // Compute whether the bar is below the parent number line.
    // center.y = bar top, visual center = y + (JUMP_BAR_BAR_HEIGHT * scale) / 2
    var lineWorldY = parentLine ? findParentLineGeometry(parentLine).lineWorldY : null;
    var currentJBScale = jumpBarScale || 1;
    var isBelow = lineWorldY !== null
        ? barPosition.y + (JUMP_BAR_BAR_HEIGHT * currentJBScale) / 2 > lineWorldY
        : false;
    // Refs to prevent stale closures in window event listeners
    var barPositionRef = useRef({ x: center.x, y: center.y });
    var barWidthRef = useRef(width);
    var barDirectionRef = useRef(direction);
    var dragRef = useRef(null);
    var hasMoved = useRef(false);
    var manipulativeRef = useRef(manipulative);
    // Multi-select move refs
    var multiSelectStartPositions = useRef([]);
    var selectedToolRef = useRef(selectedTool);
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    useEffect(function () {
        selectedToolRef.current = selectedTool;
    }, [selectedTool]);
    // Keep localTextRef in sync with localText state
    useEffect(function () {
        localTextRef.current = localText;
    }, [localText]);
    // Sync localText from props when not editing (undo/redo, toggle)
    useEffect(function () {
        var _a;
        if (!isEditorFocused) {
            var newText = ((_a = data.text) === null || _a === void 0 ? void 0 : _a.length) ? data.text : [{ type: 'text', value: '' }];
            setLocalText(newText);
            localTextRef.current = newText;
        }
    }, [data.text]);
    useEffect(function () {
        setBarPosition({ x: center.x, y: center.y });
        setBarWidth(width);
        setBarDirection(direction);
        barPositionRef.current = { x: center.x, y: center.y };
        barWidthRef.current = width;
        barDirectionRef.current = direction;
    }, [center.x, center.y, width, direction]);
    // TEMP DIAGNOSTIC (JumpBar typing-jump): log whenever the values that drive
    // the bar's vertical placement change, so we can see the transient flip.
    // Commented out — re-enable if the jump regresses.
    // useEffect(() => {
    //   console.log('[JB:geom]', id?.slice?.(-4), {
    //     barY: Math.round(barPosition.y),
    //     barX: Math.round(barPosition.x),
    //     barWidth: Math.round(barWidth),
    //     isBelow,
    //     scale: jumpBarScale || 1,
    //   });
    // }, [barPosition.y, barPosition.x, barWidth, isBelow, jumpBarScale, id]);
    // --- UPDATE HELPER ---
    var updateJumpBar = useCallback(function (updates) {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.id !== id)
                    return item;
                var resolved = typeof updates === 'function' ? updates(item.data) : updates;
                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), resolved) });
            });
        });
    }, [id, setManipulative]);
    // Click-outside to close editor and commit text
    useEffect(function () {
        if (!isEditorFocused)
            return;
        var handleClickOutside = function (e) {
            var _a, _b, _c;
            var target = e.target;
            if (((_a = labelContainerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) ||
                target.closest('.ML__keyboard') ||
                target.closest('[part="keyboard"]') ||
                target.closest('.keypadWrapper')) {
                return;
            }
            setIsEditorFocused(false);
            // Prefer live DOM content from the editor ref over the potentially stale
            // localTextRef (stale when getMathData was blocked by keyboard-visible guard).
            var currentText = (_c = (_b = mathEditorRef.current) === null || _b === void 0 ? void 0 : _b.getContent()) !== null && _c !== void 0 ? _c : localTextRef.current;
            // If the parent line has "snap jumps to underlying grid" on AND the
            // typed value resolves to a width that isn't a clean grid-square
            // multiple, snap the bar to the closest grid square and replace the
            // label with the snapped value. e.g. on a Fraction Tiles line
            // (12 grid squares per unit), typing "0.9" → 594 px → snaps to
            // 605 px (11 grid squares) → label becomes "0.92".
            var textToCommit = currentText;
            var snapUpdates = {};
            if (jumpBarSnapToGrid &&
                (parentLine === null || parentLine === void 0 ? void 0 : parentLine.lineMode) !== 'noTickMarks' &&
                pxPerUnit > 0) {
                var labelTextRaw = (Array.isArray(currentText) ? currentText : [])
                    .map(function (seg) {
                    var _a, _b, _c;
                    return (seg === null || seg === void 0 ? void 0 : seg.type) === 'text'
                        ? String((_a = seg.value) !== null && _a !== void 0 ? _a : '')
                        : String((_c = (_b = seg === null || seg === void 0 ? void 0 : seg.latex) !== null && _b !== void 0 ? _b : seg === null || seg === void 0 ? void 0 : seg.value) !== null && _c !== void 0 ? _c : '');
                })
                    .join('')
                    .trim();
                if (labelTextRaw !== '' && /[0-9]/.test(labelTextRaw)) {
                    var raw = parseLabelToNumber(labelTextRaw);
                    var numericValue = Math.abs(raw);
                    if (Number.isFinite(numericValue) && numericValue > 0) {
                        var rawWidth = numericValue * pxPerUnit;
                        // Snap step is one underlying grid square — bar end aligns
                        // with the canvas grid regardless of number-type. The bar's
                        // value is then `snappedWidth / pxPerUnit` (e.g. 1/12 unit on
                        // a Fraction Tiles line, 1 unit on a normal line).
                        var snappedWidth = Math.max(GRID_SIZE, Math.round(rawWidth / GRID_SIZE) * GRID_SIZE);
                        // Only act when snapping actually changes the width (>0.5 px to
                        // avoid float-noise no-ops).
                        if (Math.abs(snappedWidth - rawWidth) > 0.5) {
                            var snappedUnitLength = snappedWidth / pxPerUnit;
                            var signedValue = (raw < 0 ? -1 : 1) * snappedUnitLength;
                            // Build math-editor segments so fractions land as stacked
                            // \frac{p}{q} instead of flat "p/q" text. Whole and decimal
                            // values still come out as text.
                            textToCommit = buildLabelSegments(signedValue, parentLine === null || parentLine === void 0 ? void 0 : parentLine.numberType);
                            // Anchor the start edge so flipping/resizing only moves the
                            // arrow side (mirrors the typed-value handler). For a
                            // forward bar the start is the left edge (= barPosition.x);
                            // for a backward bar it's the right edge.
                            var scaleLocal = jumpBarScale || 1;
                            var oldDirection = barDirectionRef.current;
                            var oldCenterX = barPositionRef.current.x;
                            var oldVisualWidth = barWidthRef.current * scaleLocal;
                            var startX = oldDirection === 'forward'
                                ? oldCenterX
                                : oldCenterX + oldVisualWidth;
                            var newDirection = raw < 0 ? 'backward' : 'forward';
                            var newVisualWidth = snappedWidth * scaleLocal;
                            var newCenterX_1 = newDirection === 'forward' ? startX : startX - newVisualWidth;
                            barWidthRef.current = snappedWidth;
                            barPositionRef.current = __assign(__assign({}, barPositionRef.current), { x: newCenterX_1 });
                            barDirectionRef.current = newDirection;
                            setBarWidth(snappedWidth);
                            setBarPosition(function (prev) { return (__assign(__assign({}, prev), { x: newCenterX_1 })); });
                            setBarDirection(newDirection);
                            setLocalText(textToCommit);
                            localTextRef.current = textToCommit;
                            snapUpdates = {
                                width: snappedWidth,
                                unitLength: snappedUnitLength,
                                direction: newDirection,
                                center: __assign({}, barPositionRef.current),
                                dimensions: __assign(__assign({}, data.dimensions), { width: snappedWidth }),
                            };
                        }
                    }
                }
            }
            var oldData = dataBeforeEditRef.current || __assign({}, data);
            var newData = __assign(__assign(__assign({}, data), { text: textToCommit }), snapUpdates);
            if (JSON.stringify(oldData) !== JSON.stringify(newData)) {
                updateJumpBar(__assign({ text: textToCommit }, snapUpdates));
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(oldData),
                    new: JSON.stringify(newData),
                });
            }
            dataBeforeEditRef.current = null;
        };
        window.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('touchstart', handleClickOutside);
        return function () {
            window.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('touchstart', handleClickOutside);
        };
    }, [isEditorFocused, data, id, updateJumpBar]);
    // Open editor mode when keyboard Tab lands directly on the label
    // text field (skipping the wrapper's Enter handler). The MathEditor's
    // own onFocus calls focusHandler → setSelectedTool([selectionType]),
    // so detecting that the selectionType is in selectedTool tells us
    // the editorDiv just gained focus. Flipping isEditorFocused=true here
    // surfaces the math editor button (gated on isEditorFocused) so it
    // becomes a real Tab target for keyboard / SR users.
    var labelSelectionType = "nl_".concat(id, "_label_0");
    useEffect(function () {
        if (!selectedTool.includes(labelSelectionType))
            return;
        if ((jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) || isLengthLocked)
            return;
        if (isEditorFocused)
            return;
        dataBeforeEditRef.current = __assign({}, data);
        setIsEditorFocused(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTool, labelSelectionType, jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing, isLengthLocked]);
    // Close editor when selectedTool no longer belongs to this jump bar
    var isThisJBActive = selectedTool.some(function (t) { return t.startsWith(id); });
    useEffect(function () {
        var _a, _b;
        if (!isEditorFocused)
            return;
        if (!isThisJBActive) {
            setIsEditorFocused(false);
            var currentText = (_b = (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.getContent()) !== null && _b !== void 0 ? _b : localTextRef.current;
            var oldData = dataBeforeEditRef.current || __assign({}, data);
            var newData = __assign(__assign({}, data), { text: currentText });
            if (JSON.stringify(oldData) !== JSON.stringify(newData)) {
                updateJumpBar({ text: currentText });
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify(oldData),
                    new: JSON.stringify(newData),
                });
            }
            dataBeforeEditRef.current = null;
        }
    }, [isThisJBActive, data, id, updateJumpBar]);
    // --- SELECTION ---
    var handleClick = useCallback(function (e) {
        e.stopPropagation();
        if (hasMoved.current)
            return;
        var isMultiSelect = e.ctrlKey || e.metaKey;
        // Select only the jump bar itself. Co-selecting the parent line made
        // Delete remove the line (and all its bars) instead of just the bar.
        // The right panel now resolves jump-bar tools from the bar's id, so
        // we no longer need to keep the line in selectedTool.
        if (isMultiSelect) {
            setSelectedTool(function (prev) {
                return prev.includes(id) ? prev.filter(function (s) { return s !== id; }) : __spreadArray(__spreadArray([], prev, true), [id], false);
            });
        }
        else {
            setSelectedTool([id]);
        }
    }, [id, setSelectedTool]);
    // Open the right-side panel whenever this jump bar transitions from
    // unselected to selected. Tracks the previous state in a ref so we only
    // fire on the rising edge — re-renders that keep `isThisJBActive` true
    // (e.g. dragging while selected) don't re-trigger the drawer.
    var wasJBActiveRef = useRef(false);
    useEffect(function () {
        if (isThisJBActive && !wasJBActiveRef.current) {
            customEventHandler.dispatchEvent('openRightDrawer', {});
        }
        wasJBActiveRef.current = isThisJBActive;
    }, [isThisJBActive]);
    // --- Helper: snap Y to parent number line ---
    // Bar is always at [y, y+JUMP_BAR_BAR_HEIGHT] regardless of flip (arc is absolute).
    // Three non-overlapping zones — edge snaps fire first (opposite-of-arrow priority):
    //   line below bar  → snap bar bottom to line (arc above = normal, opposite of arrow)
    //   line above bar  → snap bar top to line    (arc below = flipped, opposite of arrow)
    //   line inside bar → center snap
    var snapYToLine = useCallback(function (y, currentX, barW) {
        if (!parentLine)
            return y;
        var _a = findParentLineGeometry(parentLine), lineWorldY = _a.lineWorldY, lineStartX = _a.lineStartX, worldPxPerUnit = _a.pxPerUnit;
        var lineEndX = lineStartX + (parentLine.endNum - parentLine.startNum) * worldPxPerUnit;
        var currentScale = parentLine.scale || 1;
        var visualBarW = barW * currentScale;
        var visualBarH = JUMP_BAR_BAR_HEIGHT * currentScale;
        // Boundary restriction: only snap if bar horizontally overlaps the line range
        if (currentX + visualBarW <= lineStartX || currentX >= lineEndX)
            return y;
        // Edge snap: line strictly below bar → snap bar bottom to line (opposite-of-arrow, high priority)
        if (lineWorldY > y + visualBarH) {
            if (Math.abs(y + visualBarH - lineWorldY) < JUMP_BAR_SNAP_THRESHOLD) {
                return lineWorldY - visualBarH;
            }
        }
        // Edge snap: line strictly above bar → snap bar top to line (opposite-of-arrow, high priority)
        if (lineWorldY < y) {
            if (Math.abs(y - lineWorldY) < JUMP_BAR_SNAP_THRESHOLD) {
                return lineWorldY;
            }
        }
        // Proximity snap: line is close to bar center → center bar on line
        var barCenterY = y + visualBarH / 2;
        if (Math.abs(barCenterY - lineWorldY) < JUMP_BAR_SNAP_THRESHOLD) {
            return lineWorldY - visualBarH / 2;
        }
        return y;
    }, [parentLine]);
    // --- Helper: snap X anchor edge to underlying canvas grid ---
    // Shifts the whole bar so the start edge (opposite-of-arrow) lands on the
    // nearest GRID_SIZE multiple — i.e. one canvas grid square. The numerical
    // value of the resulting jump is `barWidth / pxPerUnit`, which equals
    // `1 grid square / pxPerUnit` per snap step:
    //   - Normal NL (interval=1)        → 1 unit per grid square.
    //   - Normal NL (interval=0.5)      → 0.5 unit per grid square.
    //   - Fraction Tiles (12/unit)      → 1/12 unit per grid square.
    //   - Colored Tiles (2/unit)        → 1/2 unit per grid square.
    // Snap is always to the canvas grid, never to tick marks — ticks may be
    // sparse (e.g. interval=2) but the user still expects fine 1-grid-square
    // resolution when this toggle is on.
    // The start (non-arrow) edge snaps to whichever is nearest:
    //   - a tick mark on the parent number line
    //   - any neighbour jump bar's start or end edge (so jumps line up
    //     even when a previous bar ended between ticks, e.g. a 4.5-length
    //     bar's end becomes a snap target for the next bar's start)
    // When the line has no tick marks we fall back to a GRID_SIZE step so
    // there's still a unit of measure to align to.
    var snapXToGrid = useCallback(function (x, w) {
        if (!parentLine)
            return x;
        var lineStartX = findParentLineGeometry(parentLine).lineStartX;
        var isForward = barDirectionRef.current === 'forward';
        var scale = parentLine.scale || 1;
        var jbScale = jumpBarScale || 1;
        // w is unscaled barWidth; right edge in world coords = x + w * jbScale
        // (the bar's OWN scale, not the parent line's — these are independent)
        var anchorX = isForward ? x : x + w * jbScale;
        var anchorRel = anchorX - lineStartX;
        // Build candidate snap positions in line-local px, grouped by type so
        // each group's own distance threshold gates it — a candidate far
        // from the anchor must not steal the snap from a closer-but-still-far
        // candidate of another type (or vice versa), and neither should fire
        // at all when nothing is within its own threshold. The regular-tick
        // group is the exception: on a ticked line it is unconstrained (see
        // below) so the bar can never rest between two ticks. Custom tick
        // marks are NOT a snap target on a ticked line at all — only this
        // line's own regular ticks may attract the bar there; custom ticks
        // remain a (threshold-gated) target only on blank lines.
        //   - the line's own regular tick marks (unconstrained on ticked lines)
        //   - custom tick marks (blank lines only, still threshold-gated)
        //   - canvas gridlines (threshold-gated, ticked-or-blank as today)
        //   - both edges of every sibling JB (start AND end), so a
        //     previous bar's end-between-ticks position is a valid target
        var regularTickCandidates = [];
        var gridlineCandidates = [];
        var blankLineCustomTickCandidates = [];
        // The grid-candidate step: use the raw, unscaled GRID_SIZE (55px) when
        // the line's own `scale` is a whole number — this is what actually
        // produces *intermediate* candidates between ticks (e.g. at scale 2,
        // tick spacing doubles to 2 grid squares' worth of px, and the raw
        // 55px step gives exactly one candidate at the midpoint). Since
        // `pxPerUnit` is always an integer multiple of GRID_SIZE
        // (`gridSquaresPerUnit * GRID_SIZE`), a whole-number `scale` guarantees
        // the raw step evenly divides the scaled tick spacing, so it can never
        // drift out of alignment. For a fractional `scale` (e.g. 0.6), the
        // raw step would NOT evenly divide the scaled tick spacing — that was
        // the original scale-mismatch bug (see Agent-logs) — so we fall back
        // to the old scaled step there, which coincides exactly with ticks
        // (no finer resolution, but never misaligned).
        // Scale+/scale- buttons increment by 0.1 repeatedly, so a "whole"
        // scale like 2 is often actually stored as e.g. 2.000000000000001 due
        // to floating-point drift — Number.isInteger would (wrongly) treat
        // that as fractional and lose the in-between grid candidates. Use a
        // tolerance instead.
        var isWholeScale = Math.abs(scale - Math.round(scale)) < 1e-6;
        var gridStep = isWholeScale ? GRID_SIZE : GRID_SIZE * scale;
        if (ticks.length > 0) {
            for (var _i = 0, ticks_1 = ticks; _i < ticks_1.length; _i++) {
                var t_1 = ticks_1[_i];
                regularTickCandidates.push(t_1.px);
            }
            // When the user opts into snap-to-underlying-grid, also add every
            // canvas gridline so the bar can land on an inter-tick position
            // (e.g. on a fraction-tile NL with interval=1/4, ticks are every
            // 3 grid squares; this lets the start snap to 1, 2, 4, 5… as well).
            if (jumpBarSnapToGrid) {
                var lineRange = (parentLine.endNum - parentLine.startNum) * pxPerUnit * scale;
                for (var v = 0; v <= lineRange + 0.5; v += gridStep)
                    gridlineCandidates.push(v);
            }
        }
        else {
            // Blank line — custom ticks remain a valid (threshold-gated) target
            // here, unlike on a ticked line.
            blankLineCustomTickCandidates.push.apply(blankLineCustomTickCandidates, customTickCandidates);
            if (playerBgPattern === 'square') {
                // The `jumpBarSnapToGrid` toggle is disabled/inert in the popup
                // for noTickMarks lines, so it can never be true here. Instead
                // fall back to GRID_SIZE multiples whenever the canvas background
                // grid is on, matching how the parent NL itself snaps to the same
                // grid when dragged (see NumberLineManipulative.tsx's
                // `playerBgPattern === 'square'` check).
                var lineRange = (parentLine.endNum - parentLine.startNum) * pxPerUnit * scale;
                for (var v = 0; v <= lineRange + 0.5; v += gridStep)
                    gridlineCandidates.push(v);
            }
        }
        var edgeCandidates = [];
        for (var _a = 0, siblingBars_1 = siblingBars; _a < siblingBars_1.length; _a++) {
            var sib = siblingBars_1[_a];
            var sibScale = sib.scale || 1;
            var sibLeft = sib.center.x - lineStartX;
            var sibRight = sibLeft + sib.width * sibScale;
            edgeCandidates.push(sibLeft, sibRight);
        }
        var nearest = function (list) {
            var bestRel = anchorRel;
            var bestDist = Infinity;
            for (var _i = 0, list_1 = list; _i < list_1.length; _i++) {
                var c = list_1[_i];
                var d = Math.abs(anchorRel - c);
                if (d < bestDist) {
                    bestDist = d;
                    bestRel = c;
                }
            }
            return { dist: bestDist, rel: bestRel };
        };
        var regularTickBest = nearest(regularTickCandidates);
        var customTickBest = nearest(blankLineCustomTickCandidates);
        var gridlineBest = nearest(gridlineCandidates);
        var edgeBest = nearest(edgeCandidates);
        // When grid-snap is on, grid lines participate in the merged
        // nearest-pick below regardless of the line's tick interval — the
        // grid-candidate step/range are already computed correctly relative
        // to the tick lattice (see the whole-scale step logic above), so
        // there's no longer a reason to gate this on `intervalNum` being
        // whole. A dense (fractional-interval) tick lattice still wins
        // nearest almost everywhere on its own merits; this just also lets
        // the grid win where it's genuinely closer (e.g. whole-unit
        // boundaries on a fractional-interval line).
        var gridIsPrimary = jumpBarSnapToGrid && gridlineCandidates.length > 0;
        var bestRel = anchorRel;
        var bestDist = Infinity;
        // Regular ticks: unconstrained on a ticked line — the bar's start edge
        // must always resolve to the nearest one, never rest in between.
        if (!gridIsPrimary && regularTickCandidates.length > 0 && regularTickBest.dist < bestDist) {
            bestDist = regularTickBest.dist;
            bestRel = regularTickBest.rel;
        }
        // When gridIsPrimary, grid lines and ticks are merged into ONE
        // unconstrained nearest-pick (never threshold-gated) — the bar must
        // always resolve to whichever of the two is closest, with no dead
        // zone in between. Threshold-gating one of them (e.g. only snapping
        // to grid within 15px) left the drag range between grid squares
        // unsnapped, so the bar would suddenly jump to a far tick once the
        // cursor left that 15px window — and flip back and forth right at
        // the boundary. Comparing both unconstrained and taking the nearest
        // makes every position on the drag resolve to a single, stable
        // target with no ambiguous zone.
        if (gridIsPrimary &&
            gridlineBest.dist < bestDist &&
            (regularTickCandidates.length === 0 || gridlineBest.dist <= regularTickBest.dist)) {
            bestDist = gridlineBest.dist;
            bestRel = gridlineBest.rel;
        }
        if (gridIsPrimary &&
            regularTickCandidates.length > 0 &&
            regularTickBest.dist < bestDist) {
            bestDist = regularTickBest.dist;
            bestRel = regularTickBest.rel;
        }
        if (customTickBest.dist <= JUMP_BAR_TICK_SNAP_THRESHOLD && customTickBest.dist < bestDist) {
            bestDist = customTickBest.dist;
            bestRel = customTickBest.rel;
        }
        if (edgeBest.dist <= JUMP_BAR_EDGE_SNAP_THRESHOLD && edgeBest.dist < bestDist) {
            bestDist = edgeBest.dist;
            bestRel = edgeBest.rel;
        }
        return x + (bestRel - anchorRel);
    }, [
        parentLine,
        ticks,
        siblingBars,
        customTickCandidates,
        pxPerUnit,
        jumpBarSnapToGrid,
        playerBgPattern,
        jumpBarScale,
    ]);
    // --- Helper: snap X edges to sibling jump bars ---
    // Opposite-of-arrow end (start of the jump) gets priority.
    var snapXToSiblings = useCallback(function (x, w) {
        if (siblingBars.length === 0)
            return x;
        var edges = collectJumpBarEdges(siblingBars, id);
        var isForward = barDirectionRef.current === 'forward';
        if (isForward) {
            // Arrow on right → snap left (start) first
            var snappedLeft = snapToJumpBarEdge(x, edges, JUMP_BAR_EDGE_SNAP_THRESHOLD);
            if (snappedLeft !== x)
                return snappedLeft;
            var snappedRight = snapToJumpBarEdge(x + w, edges, JUMP_BAR_EDGE_SNAP_THRESHOLD);
            if (snappedRight !== x + w)
                return snappedRight - w;
        }
        else {
            // Arrow on left → snap right (start) first
            var snappedRight = snapToJumpBarEdge(x + w, edges, JUMP_BAR_EDGE_SNAP_THRESHOLD);
            if (snappedRight !== x + w)
                return snappedRight - w;
            var snappedLeft = snapToJumpBarEdge(x, edges, JUMP_BAR_EDGE_SNAP_THRESHOLD);
            if (snappedLeft !== x)
                return snappedLeft;
        }
        return x;
    }, [siblingBars, id]);
    // --- DRAG BAR ---
    var onBarMouseDown = useCallback(function (e) {
        // Only `lock.positioning` (and the catch-all `lock.editing`) block
        // drag. `lock.length` freezes the bar's *length* — its end knobs and
        // its label-driven length editing — but the bar should still be
        // movable along the line and liftable off it. Keeping length lock
        // out of this check lets the teacher pin a bar's size while still
        // sliding it to compare different starting points.
        if ((jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.positioning) || (jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing))
            return;
        e.stopPropagation();
        e.preventDefault();
        var clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        var clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        dragRef.current = {
            startX: clientX,
            startY: clientY,
            origX: barPositionRef.current.x,
            origY: barPositionRef.current.y,
        };
        hasMoved.current = false;
        customEventHandler.dispatchEvent('numberLineDragActive', [id]);
        // Multi-select: capture start positions of all selected jump bars and number lines.
        // Uses selectedToolRef/manipulativeRef (not the plain selectedTool/manipulative closure
        // vars) to match onMove's/onUp's gate below — this callback can be a render behind if the
        // jump bar was just added to (or removed from) the selection in the same gesture, so the
        // plain `selectedTool` closure here can disagree with the real current selection. Confirmed
        // live: logging both showed selectedTool=[] while selectedToolRef=[6 ids] at the exact
        // mousedown that failed to broadcast — 'multiSelectMoveStart' never fired (so the central
        // engine's follower snapshot was never captured) while 'multiSelectMove' fired every frame
        // during the drag — every follower's delta was silently dropped the whole time. Mirrors the
        // identical fix in NumberLineManipulative.tsx's onMouseDown.
        if (selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id)) {
            multiSelectStartPositions.current = manipulativeRef.current
                .filter(function (item) {
                return item.data.id !== undefined &&
                    selectedToolRef.current.includes(item.data.id) &&
                    (item.data.type === 'jumpBar' || item.data.type === 'numberLine');
            })
                .map(function (item) { return ({
                id: item.data.id,
                center: __assign({}, item.data.center),
            }); });
            customEventHandler.dispatchEvent('multiSelectMoveStart', { sourceId: id });
        }
        var onMove = function (ev) {
            if (!dragRef.current)
                return;
            var cx = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
            var cy = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
            var dx = cx - dragRef.current.startX;
            var dy = cy - dragRef.current.startY;
            if (!hasMoved.current && Math.abs(dx) + Math.abs(dy) < 5)
                return;
            hasMoved.current = true;
            var newX = dragRef.current.origX + dx / zoomLevel;
            var newY = dragRef.current.origY + dy / zoomLevel;
            var isMultiSelect = selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id);
            // Skip snapping in multi-select mode to keep movement synchronized
            if (!isMultiSelect) {
                // Y snap is proximity-based (only fires within JUMP_BAR_SNAP_THRESHOLD
                // of the line). Runs regardless of the `jumpBarSnapToGrid`
                // toggle so the bar drops onto the line whenever the user
                // brings it close.
                newY = snapYToLine(newY, newX, barWidthRef.current);
                // Start (non-arrow edge) ALWAYS snaps to the nearest available
                // anchor — that's the closer of: a tick mark on the line, or
                // a sibling bar's edge. The unified snapXToGrid handles both
                // candidate sets in one pass.
                newX = snapXToGrid(newX, barWidthRef.current);
            }
            // Position constraint: the bar's START (non-arrow) edge is always
            // fully contained within the line's width — it cannot be dragged
            // before lineStartX or past lineEndX, whether the bar is sitting on
            // the line or floating above/below it. The bar's ARROW edge is left
            // unrestricted — a bar longer than the remaining line length can
            // still extend past lineEndX/lineStartX with its arrow tip. Which
            // DOM edge is the "start" depends on `direction`: forward bars have
            // the arrow on the right (start = left/`newX`), backward bars have
            // the arrow on the left (start = right/`newX + visualWidth`).
            if (!isMultiSelect && parentLine) {
                var _a = findParentLineGeometry(parentLine), lineStartX = _a.lineStartX, worldPxPerUnit = _a.pxPerUnit;
                var lineEndX = lineStartX + (parentLine.endNum - parentLine.startNum) * worldPxPerUnit;
                var isForward_1 = barDirectionRef.current === 'forward';
                if (isForward_1) {
                    if (newX > lineEndX)
                        newX = lineEndX;
                    if (newX < lineStartX)
                        newX = lineStartX;
                }
                else {
                    var visualWidth = barWidthRef.current * (jumpBarScale || 1);
                    if (newX + visualWidth > lineEndX)
                        newX = lineEndX - visualWidth;
                    if (newX + visualWidth < lineStartX)
                        newX = lineStartX - visualWidth;
                }
            }
            barPositionRef.current = { x: newX, y: newY };
            setBarPosition({ x: newX, y: newY });
            // Multi-select: broadcast delta so other selected jump bars follow
            if (isMultiSelect) {
                customEventHandler.dispatchEvent('multiSelectMove', {
                    deltaX: newX - dragRef.current.origX,
                    deltaY: newY - dragRef.current.origY,
                    sourceId: id,
                });
            }
        };
        var onUp = function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            var isMultiSelectSource = selectedToolRef.current.length > 1 && selectedToolRef.current.includes(id);
            if (hasMoved.current && isMultiSelectSource) {
                // Multi-select: batch saveState for all selected jump bars (single undo step).
                // Bug fix: this branch previously only computed the undo entry from the leader's
                // local drag-tracking ref/state — it never actually committed the jumpBar's own
                // new position into the shared manipulative array via updateJumpBar(). That left
                // data.center silently unchanged (confirmed live: MATCH:false between the local
                // ref and the data.center prop right here), so an Undo's "revert to old" was a
                // no-op — the array already held "old" the whole time — and the DOM never reset
                // either, since the prop-sync effect only fires when center.x/center.y actually
                // change. Mirrors the solo-drag branch below: commit center + re-derive
                // snappedPosition before building the save entries.
                var newCenter = __assign({}, barPositionRef.current);
                var newSnappedPosition = null;
                if (parentLine) {
                    var currentScale = jumpBarScale || 1;
                    var halfLineW = ((parentLine.endNum - parentLine.startNum) * pxPerUnit) / 2;
                    var lineStartVisual = parentLine.center.x - halfLineW * currentScale;
                    newSnappedPosition = parseFloat((parentLine.startNum +
                        (newCenter.x - lineStartVisual) / (pxPerUnit * currentScale)).toFixed(6));
                }
                updateJumpBar({ center: newCenter, snappedPosition: newSnappedPosition });
                var selfStart = multiSelectStartPositions.current.find(function (p) { return p.id === id; });
                if (selfStart) {
                    var selfDeltaX_1 = newCenter.x - selfStart.center.x;
                    var selfDeltaY_1 = newCenter.y - selfStart.center.y;
                    var batchSave = multiSelectStartPositions.current.map(function (startItem) { return ({
                        type: startItem.id,
                        id: startItem.id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(startItem.center),
                        new: JSON.stringify({
                            x: startItem.center.x + selfDeltaX_1,
                            y: startItem.center.y + selfDeltaY_1,
                        }),
                    }); });
                    if (!registerMultiSelectSaveEntries(id, batchSave)) {
                        saveState(batchSave);
                    }
                }
                customEventHandler.dispatchEvent('multiSelectMoveEnd', { sourceId: id });
            }
            else if (hasMoved.current) {
                var oldData = __assign({}, data);
                var newCenter = __assign({}, barPositionRef.current);
                var newSnappedPosition = null;
                if (parentLine) {
                    var currentScale = jumpBarScale || 1;
                    var halfLineW = ((parentLine.endNum - parentLine.startNum) * pxPerUnit) / 2;
                    var lineStartVisual = parentLine.center.x - halfLineW * currentScale;
                    newSnappedPosition = parseFloat((parentLine.startNum +
                        (newCenter.x - lineStartVisual) / (pxPerUnit * currentScale)).toFixed(6));
                }
                updateJumpBar({ center: newCenter, snappedPosition: newSnappedPosition });
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var alreadyAtFront = beforeIds[beforeIds.length - 1] === id;
                var batchEntries = [
                    {
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'center',
                        old: JSON.stringify(oldData.center),
                        new: JSON.stringify(newCenter),
                    },
                ];
                if (!alreadyAtFront) {
                    // Functional update against the live `prev` — updateJumpBar just
                    // above already queued its own functional setManipulative call;
                    // replacing state with objects built from manipulativeRef.current
                    // here would silently clobber that fresh center/snappedPosition data.
                    batchEntries.push({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(__spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return bid !== id; }), true), [id], false)),
                    });
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, [id]); });
                }
                saveState(batchEntries);
            }
            dragRef.current = null;
            customEventHandler.dispatchEvent('numberLineDragActive', []);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
    }, [
        zoomLevel,
        data,
        id,
        updateJumpBar,
        snapYToLine,
        snapXToGrid,
        snapXToSiblings,
        parentLine,
        jumpBarScale,
        pxPerUnit,
        jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.positioning,
        jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing,
    ]);
    // --- RESIZE from handles ---
    var onHandleMouseDown = useCallback(function (side, e) {
        var _a, _b, _c;
        if (isLengthLocked)
            return;
        e.stopPropagation();
        e.preventDefault();
        // Always strip the label selection and blur the editor before the
        // resize starts. On iPad, isEditorFocused (React state) can be false
        // while the editorDiv remains document.activeElement — MathEditor's
        // activeElement guard then blocks the label update that onUp triggers
        // via setLocalText. Unconditionally checking all three conditions
        // (React state, selectedTool, DOM focus) ensures the guards clear
        // regardless of which one is stale.
        var editorSelectionPrefix = "nl_".concat(id, "_label");
        var hasLabelSelection = selectedTool.some(function (s) { return s.startsWith(editorSelectionPrefix); });
        var editorHasDOMFocus = labelContainerRef.current
            ? labelContainerRef.current.contains(document.activeElement)
            : false;
        if (isEditorFocused || hasLabelSelection || editorHasDOMFocus) {
            setIsEditorFocused(false);
            setSelectedTool(function (prev) {
                return prev.filter(function (s) { return !s.startsWith(editorSelectionPrefix); });
            });
            (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.closeKeyboard();
            (_c = (_b = document.activeElement) === null || _b === void 0 ? void 0 : _b.blur) === null || _c === void 0 ? void 0 : _c.call(_b);
        }
        var startClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        var origWidth = barWidthRef.current;
        var origX = barPositionRef.current.x;
        var origDirection = barDirectionRef.current;
        var currentScale = jumpBarScale || 1;
        var onMove = function (ev) {
            // Work in visual (parent) space — delta divided by zoom only, not scale
            var clientX = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
            var deltaXVisual = (clientX - startClientX) / zoomLevel;
            // Visual edges of the bar
            var visualLeft = origX;
            var visualRight = origX + origWidth * currentScale;
            // Anchor = non-dragged edge (in visual/parent space)
            var anchorX = side === 'right' ? visualLeft : visualRight;
            // Dragged edge moves in visual space
            var draggedX = side === 'right' ? visualRight + deltaXVisual : visualLeft + deltaXVisual;
            // Snap dragged endpoint to sibling edges (parent space)
            if (siblingBars.length > 0) {
                var edges = collectJumpBarEdges(siblingBars, id);
                draggedX = snapToJumpBarEdge(draggedX, edges, JUMP_BAR_EDGE_SNAP_THRESHOLD);
            }
            // Snap the dragged endpoint to the nearest tick mark / custom tick /
            // canvas gridline (nearest-wins per candidate group, mirroring
            // `snapXToGrid`'s structure). The line's own regular ticks win
            // unconditionally whenever the line has ticks — the dragged edge
            // must never rest between two ticks. On a ticked line, custom ticks
            // are NOT a snap target at all (only on blank lines, threshold-gated,
            // same as before).
            var isBlankLine = (parentLine === null || parentLine === void 0 ? void 0 : parentLine.lineMode) === 'noTickMarks';
            var hasTicks = !isBlankLine && ticks.length > 0;
            if (parentLine) {
                var lineStartX = findParentLineGeometry(parentLine).lineStartX;
                var draggedRel_1 = draggedX - lineStartX;
                var gridlineCandidates = [];
                var blankLineCustomTickCandidates = isBlankLine ? customTickCandidates : [];
                if (jumpBarSnapToGrid || (isBlankLine && playerBgPattern === 'square')) {
                    // Blank lines (no ticks) fall back to the canvas background grid
                    // whenever it's on — the `jumpBarSnapToGrid` toggle is
                    // disabled/inert in the popup for noTickMarks lines, so it can
                    // never be true for them; the canvas grid check covers that case.
                    // Step is the raw, unscaled GRID_SIZE when `parentScale` is a
                    // whole number — see the identical fix/comment in `snapXToGrid`
                    // above for why this produces real in-between candidates at
                    // whole-number scale without drifting out of alignment.
                    var lineRangeCanvas = (parentLine.endNum - parentLine.startNum) * pxPerUnit * parentScale;
                    // See the tolerance comment in `snapXToGrid` above — scale+/-
                    // drift means `parentScale` is rarely an exact integer even at
                    // a visually-whole scale.
                    var isWholeScale = Math.abs(parentScale - Math.round(parentScale)) < 1e-6;
                    var gridStep = isWholeScale ? GRID_SIZE : GRID_SIZE * parentScale;
                    for (var v = 0; v <= lineRangeCanvas + 0.5; v += gridStep)
                        gridlineCandidates.push(v);
                }
                // Nearest-wins across every candidate group, including a
                // candidate that coincides with the anchor — a 0-length jump bar
                // is a valid resting state, so the dragged edge must still snap
                // there rather than being left at whatever raw, unsnapped pixel
                // the mouse happened to be at (which is what used to happen right
                // next to the anchor's own tick, e.g. the anchor sitting at 0).
                var nearestOf = function (list) {
                    var bestRel = draggedRel_1;
                    var bestDist = Infinity;
                    for (var _i = 0, list_2 = list; _i < list_2.length; _i++) {
                        var c = list_2[_i];
                        var d = Math.abs(draggedRel_1 - c);
                        if (d < bestDist) {
                            bestDist = d;
                            bestRel = c;
                        }
                    }
                    return { dist: bestDist, rel: bestRel };
                };
                var regularTickBest = nearestOf(ticks.map(function (t) { return t.px; }));
                var customTickBest = nearestOf(blankLineCustomTickCandidates);
                var gridlineBest = nearestOf(gridlineCandidates);
                // Mirrors snapXToGrid: grid lines participate in the merged
                // nearest-pick whenever grid-snap is on, regardless of the
                // line's tick interval — see the matching comment there.
                var gridIsPrimary = hasTicks && jumpBarSnapToGrid && gridlineCandidates.length > 0;
                var bestRel = draggedRel_1;
                var bestDist = Infinity;
                if (!gridIsPrimary && hasTicks && ticks.length > 0 && regularTickBest.dist < bestDist) {
                    bestDist = regularTickBest.dist;
                    bestRel = regularTickBest.rel;
                }
                // When gridIsPrimary, grid lines and ticks are merged into ONE
                // unconstrained nearest-pick — see the matching comment in
                // `snapXToGrid` above for why threshold-gating either one leaves a
                // dead zone that causes the dragged edge to jump to a far tick and
                // oscillate at the grid line's threshold boundary.
                if (gridIsPrimary &&
                    gridlineBest.dist < bestDist &&
                    (!hasTicks || ticks.length === 0 || gridlineBest.dist <= regularTickBest.dist)) {
                    bestDist = gridlineBest.dist;
                    bestRel = gridlineBest.rel;
                }
                if (gridIsPrimary && hasTicks && ticks.length > 0 && regularTickBest.dist < bestDist) {
                    bestDist = regularTickBest.dist;
                    bestRel = regularTickBest.rel;
                }
                if (customTickBest.dist <= JUMP_BAR_TICK_SNAP_THRESHOLD &&
                    customTickBest.dist < bestDist) {
                    bestDist = customTickBest.dist;
                    bestRel = customTickBest.rel;
                }
                if (bestDist < Infinity) {
                    draggedX = lineStartX + bestRel;
                }
            }
            // Position constraint: when the handle being dragged is the START
            // (non-arrow) edge, clamp it to the parent line's
            // [lineStartX, lineEndX] — mirrors the whole-bar move-drag clamp in
            // `onBarMouseDown`. Which physical handle is the "start" depends on
            // direction: forward bars have the arrow on the right (start =
            // left handle); backward bars have the arrow on the left (start =
            // right handle). The ARROW handle is left free to extend past the
            // line; rendering masks that overflow via `clipPath` (see below).
            if (parentLine) {
                var _a = findParentLineGeometry(parentLine), lineStartX = _a.lineStartX, worldPxPerUnit = _a.pxPerUnit;
                var lineEndX = lineStartX + (parentLine.endNum - parentLine.startNum) * worldPxPerUnit;
                var isStartHandle = (side === 'left' && origDirection === 'forward') ||
                    (side === 'right' && origDirection === 'backward');
                if (isStartHandle) {
                    if (draggedX > lineEndX)
                        draggedX = lineEndX;
                    if (draggedX < lineStartX)
                        draggedX = lineStartX;
                }
            }
            // Crossed-handle detection
            var draggedIsRight = draggedX >= anchorX;
            var handlesCrossed = side === 'right' ? !draggedIsRight : draggedIsRight;
            var newDirection = handlesCrossed
                ? origDirection === 'forward'
                    ? 'backward'
                    : 'forward'
                : origDirection;
            // Visual width → local width (divide by scale for storage).
            // The dragged endpoint is already snapped above (if toggle on);
            // if the toggle is off the length is whatever the user dragged to.
            var visualWidth = Math.abs(draggedX - anchorX);
            var localWidth = visualWidth / currentScale;
            var finalWidth = localWidth;
            // New CSS left: anchor stays fixed visually, left adjusts
            var newX = draggedIsRight ? anchorX : anchorX - finalWidth * currentScale;
            barWidthRef.current = finalWidth;
            barPositionRef.current = __assign(__assign({}, barPositionRef.current), { x: newX });
            barDirectionRef.current = newDirection;
            setBarWidth(finalWidth);
            setBarPosition(function (prev) { return (__assign(__assign({}, prev), { x: newX })); });
            setBarDirection(newDirection);
        };
        var onUp = function () {
            var _a;
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            var newUnitLength = barWidthRef.current / pxPerUnit;
            var oldData = __assign({}, data);
            var newSnappedPosition = null;
            if (parentLine) {
                var currentScale_1 = jumpBarScale || 1;
                var halfLineW = ((parentLine.endNum - parentLine.startNum) * pxPerUnit) / 2;
                var lineStartVisual = parentLine.center.x - halfLineW * currentScale_1;
                newSnappedPosition = parseFloat((parentLine.startNum +
                    (barPositionRef.current.x - lineStartVisual) / (pxPerUnit * currentScale_1)).toFixed(6));
            }
            // D1: when the JB's existing label was a numeric value, mirror the
            // resize back into the label so the displayed number always matches
            // the bar's actual length. We only overwrite numeric labels — a
            // teacher-typed custom label like "x" or "twice" stays put.
            // `showNumericalValue` mode auto-renders unitLength so it doesn't
            // need a stored text update; we still don't *clobber* its custom
            // text array if any.
            //
            // Read from the React state (localTextRef) rather than the live
            // editor DOM. After the previous resize replaced the editor's
            // contents, mathEditorRef.getNumericValue() walks an in-flight DOM
            // that briefly returns a stale or null value, which left the second
            // and subsequent resizes without a label refresh ("typed 1 → resize
            // → 0.92 → resize → still 0.92" repro).
            var labelTextRaw = ((_a = localTextRef.current) !== null && _a !== void 0 ? _a : [])
                .map(function (seg) { var _a, _b, _c; return (seg === null || seg === void 0 ? void 0 : seg.type) === 'text' ? String((_a = seg.value) !== null && _a !== void 0 ? _a : '') : String((_c = (_b = seg === null || seg === void 0 ? void 0 : seg.latex) !== null && _b !== void 0 ? _b : seg === null || seg === void 0 ? void 0 : seg.value) !== null && _c !== void 0 ? _c : ''); })
                .join('')
                .trim();
            var labelAsNumber = labelTextRaw === '' ? null : parseLabelToNumber(labelTextRaw);
            var labelWasNumeric = !showNumericalValue &&
                labelAsNumber !== null &&
                Number.isFinite(labelAsNumber) &&
                // parseLabelToNumber returns 0 for unparseable strings, so reject
                // labels that don't actually contain digits.
                /[0-9]/.test(labelTextRaw) &&
                (parentLine === null || parentLine === void 0 ? void 0 : parentLine.lineMode) !== 'noTickMarks';
            var textUpdate;
            if (labelWasNumeric) {
                // Build the label as math-editor segments so a fraction renders
                // as a stacked `\frac{p}{q}` (and a mixed number as
                // "w \frac{p}{q}"), instead of the flat text "13/2". Whole
                // values and decimals stay plain text. Negative direction adds
                // a "-" prefix segment.
                var parentNumberType = parentLine === null || parentLine === void 0 ? void 0 : parentLine.numberType;
                var signedValue = (barDirectionRef.current === 'backward' ? -1 : 1) * Math.abs(newUnitLength);
                textUpdate = buildLabelSegments(signedValue, parentNumberType);
                setLocalText(textUpdate);
                localTextRef.current = textUpdate;
            }
            var updatedFields = __assign({ center: __assign({}, barPositionRef.current), width: barWidthRef.current, unitLength: parseFloat(newUnitLength.toFixed(6)), direction: barDirectionRef.current, snappedPosition: newSnappedPosition }, (textUpdate ? { text: textUpdate } : {}));
            updateJumpBar(updatedFields);
            saveState({
                type: id,
                id: id.split('_')[1],
                propertyName: 'completeData',
                old: JSON.stringify(oldData),
                new: JSON.stringify(__assign(__assign({}, oldData), updatedFields)),
            });
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
    }, [
        isLengthLocked,
        isEditorFocused,
        setSelectedTool,
        zoomLevel,
        jumpBarScale,
        jumpBarSnapToGrid,
        pxPerUnit,
        parentScale,
        ticks,
        data,
        id,
        updateJumpBar,
        siblingBars,
        customTickCandidates,
        parentLine,
        playerBgPattern,
    ]);
    // --- Feature 5: Precise length input ---
    var applyLengthInput = useCallback(function (value) {
        setIsLengthEditing(false);
        var parsed = parseValueToNumber(value);
        if (parsed <= 0 || isNaN(parsed))
            return;
        var newWidth = parsed * pxPerUnit;
        var oldData = __assign({}, data);
        var updatedFields = {
            width: newWidth,
            unitLength: parsed,
            dimensions: __assign(__assign({}, data.dimensions), { width: newWidth }),
        };
        updateJumpBar(updatedFields);
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'completeData',
            old: JSON.stringify(oldData),
            new: JSON.stringify(__assign(__assign({}, oldData), updatedFields)),
        });
    }, [pxPerUnit, data, id, updateJumpBar]);
    // --- Multi-select listener: respond when another selected jump bar is dragged ---
    useEffect(function () {
        if (!selectedTool.includes(id) || selectedTool.length <= 1)
            return;
        var myStart = { x: barPositionRef.current.x, y: barPositionRef.current.y };
        var onStart = function (_a) {
            var sourceId = _a.sourceId;
            if (sourceId === id)
                return;
            myStart = { x: barPositionRef.current.x, y: barPositionRef.current.y };
        };
        var onMove = function (_a) {
            var deltaX = _a.deltaX, deltaY = _a.deltaY, sourceId = _a.sourceId;
            if (sourceId === id || (jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.positioning))
                return;
            var newX = myStart.x + deltaX;
            var newY = myStart.y + deltaY;
            barPositionRef.current = { x: newX, y: newY };
            setBarPosition({ x: newX, y: newY });
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    return item.data.id === id
                        ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: { x: newX, y: newY } }) }) : item;
                });
            });
        };
        customEventHandler.addEventListener('multiSelectMoveStart', onStart);
        customEventHandler.addEventListener('multiSelectMove', onMove);
        return function () {
            customEventHandler.removeEventListener('multiSelectMoveStart', onStart);
            customEventHandler.removeEventListener('multiSelectMove', onMove);
        };
        // barPositionRef keeps position current without putting it in deps (avoids mid-drag re-runs)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTool, id, jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.positioning, setManipulative]);
    // --- MathEditor handlers ---
    var addMathText = useCallback(function () {
        setTimeout(function () {
            var _a;
            // focusKeypadOnOpen lands keyboard focus on the first keypad
            // button when it mounts, so SR + keyboard users are trapped in
            // the keypad until Escape (matches the WCAG focus-trap pattern).
            // Touch / mouse users still see the keypad without their focus
            // being pulled around.
            (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.insertMath({ focusKeypadOnOpen: true });
        }, 10);
    }, []);
    var handleTextChange = useCallback(function (content, _type, _index) {
        // TEMP DIAGNOSTIC (JumpBar typing-jump) — commented out; re-enable if needed
        // console.log('[JB:textChange]', id?.slice?.(-4), {
        //   content,
        //   barY: Math.round(barPositionRef.current.y),
        //   barWidth: Math.round(barWidthRef.current),
        // });
        var sanitize = function (val) { return (val ? val.replace(/<br\s*\/?>/gi, '') : ''); };
        var newText = typeof content === 'string' ? [{ type: 'text', value: sanitize(content) }] : content;
        setLocalText(newText);
        localTextRef.current = newText;
        // D2: when the parent NL has no tick marks (blank), the label is purely
        // decorative — there's no unit system to map a number to a length. Skip
        // the auto-resize entirely and just persist the typed text.
        if ((parentLine === null || parentLine === void 0 ? void 0 : parentLine.lineMode) === 'noTickMarks') {
            updateJumpBar({ text: newText });
            return;
        }
        // Length is locked — persist whatever the user types as label text but
        // refuse to resize the bar from a typed numeric value.
        if (isLengthLocked) {
            updateJumpBar({ text: newText });
            return;
        }
        // Resize jump bar if the entered value is a valid number.
        // Read from the just-set newText (React state proxy) instead of the
        // editor's getNumericValue() — getNumericValue uses parseNumericLatex
        // which only matches integers and LaTeX fractions, so decimals like
        // "0.9" parse as invalid and the bar never resizes. parseValueToNumber
        // handles ints, decimals, simple fractions, and mixed numbers.
        var labelTextRaw = newText
            .map(function (seg) {
            var _a, _b, _c;
            return (seg === null || seg === void 0 ? void 0 : seg.type) === 'text'
                ? String((_a = seg.value) !== null && _a !== void 0 ? _a : '')
                : String((_c = (_b = seg === null || seg === void 0 ? void 0 : seg.latex) !== null && _b !== void 0 ? _b : seg === null || seg === void 0 ? void 0 : seg.value) !== null && _c !== void 0 ? _c : '');
        })
            .join('')
            .trim();
        var hasDigits = /[0-9]/.test(labelTextRaw);
        if (hasDigits) {
            var raw = parseLabelToNumber(labelTextRaw);
            // Honor the typed value verbatim — even on an integer-configured
            // line, "4.5" should produce a 4.5-unit bar. Number-type only
            // governs how the auto-derived label is FORMATTED (formatJumpBarLength
            // drops trailing ".0", renders fractions on a fraction line, etc.),
            // not what values the user is allowed to type.
            var numericValue_1 = Math.abs(raw);
            var newDirection_1 = raw < 0 ? 'backward' : 'forward';
            if (numericValue_1 > 0) {
                var newWidth_1 = numericValue_1 * pxPerUnit;
                // Anchor the "start" of the jump (the non-arrow edge) so flipping
                // the sign keeps that edge pinned at the same NL position and only
                // the arrow side moves. Forward bars start at the left edge
                // (= center.x); backward bars start at the right edge
                // (= center.x + visualWidth). center.x is the bar's parent-space
                // top-left; width is local pre-scale, scaled by `jumpBarScale`.
                var scale = jumpBarScale || 1;
                var oldDirection = barDirectionRef.current;
                var oldCenterX = barPositionRef.current.x;
                var oldVisualWidth = barWidthRef.current * scale;
                var startX = oldDirection === 'forward' ? oldCenterX : oldCenterX + oldVisualWidth;
                var newVisualWidth = newWidth_1 * scale;
                var newCenterX_2 = newDirection_1 === 'forward' ? startX : startX - newVisualWidth;
                // Mirror snappedPosition to the new left-edge NL coordinate so the
                // saved snap and the visual position stay consistent. Only adjust
                // when the bar was already snapped — don't accidentally snap a
                // floating bar.
                var wasSnapped = data.snappedPosition !== null && data.snappedPosition !== undefined;
                var newSnappedPosition_1;
                if (wasSnapped && parentLine) {
                    var halfLineW = ((parentLine.endNum - parentLine.startNum) * pxPerUnit) / 2;
                    var lineStartVisual = parentLine.center.x - halfLineW * scale;
                    newSnappedPosition_1 = parseFloat((parentLine.startNum + (newCenterX_2 - lineStartVisual) / (pxPerUnit * scale)).toFixed(6));
                }
                barWidthRef.current = newWidth_1;
                barPositionRef.current = __assign(__assign({}, barPositionRef.current), { x: newCenterX_2 });
                barDirectionRef.current = newDirection_1;
                setBarWidth(newWidth_1);
                setBarPosition(function (prev) { return (__assign(__assign({}, prev), { x: newCenterX_2 })); });
                setBarDirection(newDirection_1);
                updateJumpBar(function (current) { return (__assign({ text: newText, width: newWidth_1, unitLength: numericValue_1, direction: newDirection_1, center: __assign(__assign({}, current.center), { x: newCenterX_2 }), dimensions: __assign(__assign({}, current.dimensions), { width: newWidth_1 }) }, (newSnappedPosition_1 !== undefined
                    ? { snappedPosition: newSnappedPosition_1 }
                    : {}))); });
            }
            else {
                updateJumpBar({ text: newText });
            }
        }
        else {
            updateJumpBar({ text: newText });
        }
    }, [pxPerUnit, updateJumpBar, parentLine, jumpBarScale, data.snappedPosition, isLengthLocked]);
    // Structured form of the fraction formatter. Returns an array of label
    // segments (text + math) suitable for MathEditor's `data.text` so a
    // simple fraction renders as a stacked `\frac{p}{q}` and a mixed
    // number as "w \frac{p}{q}" — matches the visual fraction format the
    // teacher writes by hand. Returns null when the value can't be
    // represented exactly within `denom`.
    var formatAsFractionSegments = function (value, denom, isMixed) {
        if (denom <= 0)
            return null;
        var sign = value < 0 ? '-' : '';
        var abs = Math.abs(value);
        var numerator = Math.round(abs * denom);
        if (Math.abs(numerator / denom - abs) > 0.005)
            return null;
        if (numerator === 0)
            return [{ type: 'text', value: '0' }];
        var g = gcd(numerator, denom);
        var n = numerator / g;
        var d = denom / g;
        if (d === 1)
            return [{ type: 'text', value: "".concat(sign).concat(n) }];
        if (isMixed && n >= d) {
            var whole = Math.floor(n / d);
            var rem = n - whole * d;
            if (rem === 0)
                return [{ type: 'text', value: "".concat(sign).concat(whole) }];
            return [
                { type: 'text', value: "".concat(sign).concat(whole, " ") },
                { type: 'math', latex: "\\frac{".concat(rem, "}{").concat(d, "}"), col: '#293273' },
            ];
        }
        var segs = [];
        if (sign)
            segs.push({ type: 'text', value: sign });
        segs.push({ type: 'math', latex: "\\frac{".concat(n, "}{").concat(d, "}"), col: '#293273' });
        return segs;
    };
    // Build a structured label for a numeric value — the math editor's
    // segment array form. Whole values stay plain text. Fractions/mixed on
    // a fraction-or-mixed line use stacked `\frac{...}{...}` math segments
    // so the label looks like a written-out fraction (e.g. "13/2"
    // displays as a 13-over-2 stack). Tries the line's natural denominator
    // first then a few common school denominators.
    var buildLabelSegments = function (value, parentNumberType) {
        var _a;
        var hasFraction = Math.abs(Math.round(value) - value) > 0.001;
        if (!hasFraction) {
            return [{ type: 'text', value: Math.round(value).toString() }];
        }
        if (parentNumberType === 1 || parentNumberType === 2) {
            // Same denominator ladder the auto label uses, so a typed/resized
            // label and the "Label Jump" label never disagree on the fraction.
            for (var _i = 0, _b = jumpBarCandidateDenominators((_a = parentLine === null || parentLine === void 0 ? void 0 : parentLine.gridSquaresPerUnit) !== null && _a !== void 0 ? _a : 1); _i < _b.length; _i++) {
                var d = _b[_i];
                var segs = formatAsFractionSegments(value, d, parentNumberType === 2);
                if (segs !== null)
                    return segs;
            }
            // Same exact-fraction fallback as formatJumpBarLength, so the typed/
            // resized label and the "Label Jump" auto label never disagree.
            var denominator = decimalToFraction(value).denominator;
            if (denominator > 0 && denominator <= JUMP_BAR_EXACT_FRACTION_MAX_DENOM) {
                var segs = formatAsFractionSegments(value, denominator, parentNumberType === 2);
                if (segs !== null)
                    return segs;
            }
        }
        // Use toFixed(2) for decimal-typed lines too — preserves 2-decimal
        // precision when present (0.45 stays "0.45") while parseFloat strips
        // trailing zeros so single-decimal values stay short (0.5 → "0.5",
        // not "0.50"). toFixed(1) would round 0.45 to 0.5 and disagree with
        // the actual bar length.
        var decimal = parseFloat(value.toFixed(2)).toString();
        return [{ type: 'text', value: decimal }];
    };
    // Display text. The auto-rendered length (showNumericalValue mode) only
    // appears once the bar is actually snapped to its parent NL — a floating
    // JB has no meaningful "unit length" to report.
    //
    // We derive the displayed value from the LIVE local state (`barWidth` /
    // `pxPerUnit` and `barDirection`) rather than from `data.unitLength` /
    // `data.direction`. This makes the label refresh:
    //   - while a resize handle is being dragged (barWidth updates each frame
    //     via setBarWidth; data.unitLength only commits on mouseup);
    //   - when the parent NL's config changes (range / interval / scaling),
    //     because pxPerUnit reflects the parent's current scaling rule.
    var isSnappedToLine = data.snappedPosition !== null && data.snappedPosition !== undefined;
    var liveUnitLength = pxPerUnit > 0 ? barWidth / pxPerUnit : unitLength;
    // Format is NumberLineLabel's — "\frac{p}{q}" / "w\frac{p}{q}" on a
    // fraction or mixed line — so the auto label renders as a stacked
    // fraction like the parent line's own tick labels instead of flat "p/q".
    var labelContent = (function () {
        var _a;
        if (!showNumericalValue || !isSnappedToLine)
            return null;
        var formatted = formatJumpBarLength(liveUnitLength, parentLine === null || parentLine === void 0 ? void 0 : parentLine.numberType, (_a = parentLine === null || parentLine === void 0 ? void 0 : parentLine.gridSquaresPerUnit) !== null && _a !== void 0 ? _a : 1);
        // A zero-length bar has no direction to report — never render "-0".
        var sign = barDirection === 'backward' && formatted !== '0' ? '-' : '';
        return "".concat(sign).concat(formatted);
    })();
    var barColor = barDirection === 'forward' ? 'jumpBarGreen' : 'jumpBarRed';
    var isForward = barDirection === 'forward';
    // Visual mask: when the bar is vertically on/near the parent line, clip
    // the colored bar AND the dashed arc bracket (with arrow) to the line's
    // horizontal range. The data length stays truthful (e.g. a 15-unit jump on
    // a 0–10 line stores 15), but only the portion that overlaps the line
    // shows. When the bar is floating clearly above/below the line, no clip is
    // applied so the full bar is visible (so the user can configure it freely
    // off-line). The label is intentionally NOT clipped — it gets its own
    // overflow-aware positioning below so it always stays inside the line.
    // Per teacher feedback: when the jump exceeds the parent NL's range we
    // *don't* visually mask the bar — the bar should render at its full width
    // and overflow past the arrowhead. The label keeps its natural centered
    // position on the full bar (no overflow-aware repositioning).
    var barClipPath = undefined;
    var labelOverlayStyle = (function () {
        return {};
    })();
    return (_jsxs("div", { ref: outerRef, className: "jumpBarManipulative ".concat(isFocused ? ' selected' : ''), "data-manipulative-id": id, style: {
            left: barPosition.x,
            top: barPosition.y,
            transform: "scale(".concat(jumpBarScale || 1, ")"),
            transformOrigin: '0 0',
            // CSS-only bring-to-front while actively dragging — permanent
            // stacking comes from array position (reordered on drop).
            zIndex: isDragging ? 9999 : undefined,
        }, "data-mfdeselect": "true", children: [_jsxs("div", { className: "jumpBarContainer".concat(isBelow ? ' flipped' : ''), style: { width: barWidth }, children: [_jsxs("div", { ref: barBodyRef, className: "jumpBarBar ".concat(barColor), onMouseDown: onBarMouseDown, onTouchStart: onBarMouseDown, onDoubleClick: function (e) {
                            if (isLengthLocked)
                                return;
                            e.stopPropagation();
                            setIsLengthEditing(true);
                        }, style: {
                            width: barWidth,
                            clipPath: barClipPath,
                            WebkitClipPath: barClipPath,
                        }, onClick: handleClick, role: "button", "aria-roledescription": jbName, "aria-label": (_e = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _e !== void 0 ? _e : "".concat(t('text.jumpBarAriaLabel', {
                            start: parentLine
                                ? ((_f = data.snappedPosition) !== null && _f !== void 0 ? _f : 0).toString()
                                : '0',
                            end: parentLine
                                ? (((_g = data.snappedPosition) !== null && _g !== void 0 ? _g : 0) + ((_h = data.unitLength) !== null && _h !== void 0 ? _h : 0)).toString()
                                : ((_j = data.unitLength) !== null && _j !== void 0 ? _j : 0).toString(),
                            length: ((_k = data.unitLength) !== null && _k !== void 0 ? _k : 0).toString(),
                        }), ". ").concat(t('text.jumpBarHint')), "aria-disabled": (jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.positioning) || (jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) ? true : undefined, tabIndex: 0, onKeyDown: function (e) {
                            if (e.target !== e.currentTarget)
                                return;
                            if (e.key !== 'Enter' && e.key !== ' ')
                                return;
                            e.preventDefault();
                            e.stopPropagation();
                            if (!selectedTool.includes(id))
                                setSelectedTool([id]);
                            var rect = e.currentTarget.getBoundingClientRect();
                            setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                        }, children: [_jsx("button", { className: "jumpBarHandle jumpBarHandleLeft", onMouseDown: function (e) { return onHandleMouseDown('left', e); }, onTouchStart: function (e) { return onHandleMouseDown('left', e); }, disabled: isLengthLocked, style: { cursor: isLengthLocked ? 'default' : 'ew-resize' }, tabIndex: -1, "aria-hidden": "true" }), _jsx("button", { className: "jumpBarHandle jumpBarHandleRight", onMouseDown: function (e) { return onHandleMouseDown('right', e); }, onTouchStart: function (e) { return onHandleMouseDown('right', e); }, disabled: isLengthLocked, style: { cursor: isLengthLocked ? 'default' : 'ew-resize' }, tabIndex: -1, "aria-hidden": "true" })] }), _jsxs("div", { className: "jumpBarArc", children: [showNumericalValue ? (_jsx("div", { className: "jumpBarLabelContent jumpBarLabelFocused", style: __assign({ top: isBelow ? '100%' : '0' }, labelOverlayStyle), children: labelContent && (
                                // NumberLineLabel renders the fraction as stacked spans, so
                                // the div's own text content would read as "1 2". Name it
                                // explicitly with the flat form. role="math" matches the
                                // house pattern for rendered math (NumberCharts' CellValue),
                                // and does NOT prune its subtree — the digit text nodes stay
                                // in the a11y tree — so the stack is hidden explicitly.
                                _jsx("div", { className: "jumpBarLabelReadOnly", role: "math", "aria-label": numberLineLabelToPlainText(labelContent), children: _jsx("span", { "aria-hidden": "true", children: _jsx(NumberLineLabel, { value: labelContent }) }) })) })) : (_jsxs("div", { ref: labelContainerRef, className: "jumpBarLabelContent ".concat(isEditorFocused || hasContent ? ' jumpBarLabelFocused' : ''), style: __assign({ top: isBelow ? '100%' : '0' }, labelOverlayStyle), onMouseDown: function (e) {
                                    e.stopPropagation();
                                    // Locked bar: don't open the editor at all. The MathEditor
                                    // is already viewonly when locked, but we also suppress the
                                    // focus transition so the keypad / math-text button never
                                    // surface. Length lock also blocks typing — the bar's
                                    // length is the only thing the editor controls, so a
                                    // length lock implies a fully read-only label too.
                                    if ((jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) || isLengthLocked)
                                        return;
                                    // Suppress the synthetic mousedown that iOS dispatches
                                    // after a touchend — the touch path already decided
                                    // whether to open the editor.
                                    if (Date.now() - labelLastTouchTimeRef.current < 500)
                                        return;
                                    // Only run the open-editor work on the rising edge. A
                                    // mousedown bubbling up from the math button (which is a
                                    // child of the label container) would otherwise re-trigger
                                    // setIsEditorFocused / focus() and race the insertMath
                                    // chain — math-field is created but the keypad never
                                    // surfaces because focus is yanked back to the editorDiv.
                                    if (!isEditorFocused) {
                                        dataBeforeEditRef.current = __assign({}, data);
                                        setIsEditorFocused(true);
                                        // Drive the MathEditor focus chain explicitly so
                                        // selectedTool immediately includes `${id}_label_0`.
                                        // Without this, the math button (gated on
                                        // isEditorFocused) flickered: the iOS-synthesized click
                                        // only arrives ~300ms after touchend and any intervening
                                        // render that recomputed isThisJBActive could knock
                                        // isEditorFocused false. Calling focus() now pins the
                                        // selection synchronously so isEditorFocused stays true
                                        // through the next render cycle.
                                        requestAnimationFrame(function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
                                    }
                                }, onTouchStart: function (e) {
                                    e.stopPropagation();
                                    if ((jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) || isLengthLocked)
                                        return;
                                    // Open the editor immediately on touchstart so the math
                                    // text icon appears on iPad without waiting for a tap-vs-
                                    // drag heuristic (which a fat-finger tap routinely failed
                                    // because finger jitter exceeded the 10 px threshold).
                                    // The label's onTouchStart already stopPropagation, so the
                                    // bar wrapper's drag listener does not receive this touch
                                    // and there's nothing to disambiguate against.
                                    labelLastTouchTimeRef.current = Date.now();
                                    // Same idempotence guard as onMouseDown above — a
                                    // touchstart bubbling up from the math button must not
                                    // re-run focus() while addMathText() is scheduling
                                    // insertMath.
                                    if (!isEditorFocused) {
                                        dataBeforeEditRef.current = __assign({}, data);
                                        setIsEditorFocused(true);
                                        // Drive the MathEditor focus chain explicitly so
                                        // selectedTool gets `${id}_label_0` synchronously and
                                        // the math button renders on the FIRST tap instead of
                                        // waiting for iOS's synthesized click.
                                        requestAnimationFrame(function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
                                    }
                                }, onTouchCancel: function () {
                                    labelLastTouchTimeRef.current = Date.now();
                                }, 
                                // --- Keyboard tab stop: the label container ---
                                // Tab to here, press Enter / Space → enter edit mode, focus
                                // moves into the MathEditor text field, math button appears.
                                // While editing (isEditorFocused), tabIndex flips to -1 so
                                // Tab doesn't bounce back to the outer container — it walks
                                // straight into the inner editorDiv and the math button.
                                //
                                // role="group" (NOT "button") because the container holds
                                // interactive descendants (MathEditor's text field and,
                                // once editing, the math editor button). ARIA forbids
                                // interactive descendants of buttons, so role="button"
                                // would hide them from screen readers — meaning SR users
                                // could never reach the math button via Next-arrow / VO
                                // navigation. The Enter / Space onKeyDown below still
                                // works regardless of role; the aria-label embeds the
                                // "Press Enter to edit label" hint so SR users still know
                                // the interaction is available.
                                role: "group", "aria-roledescription": t('text.jumpBarLabel'), "aria-label": (_l = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _l !== void 0 ? _l : "".concat(t('text.jumpBarAriaLabel', {
                                    start: parentLine
                                        ? ((_m = data.snappedPosition) !== null && _m !== void 0 ? _m : 0).toString()
                                        : '0',
                                    end: parentLine
                                        ? (((_o = data.snappedPosition) !== null && _o !== void 0 ? _o : 0) + ((_p = data.unitLength) !== null && _p !== void 0 ? _p : 0)).toString()
                                        : ((_q = data.unitLength) !== null && _q !== void 0 ? _q : 0).toString(),
                                    length: ((_r = data.unitLength) !== null && _r !== void 0 ? _r : 0).toString(),
                                }), ". ").concat(t('text.jumpBarLabelHint')), "aria-disabled": (jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) || isLengthLocked ? true : undefined, tabIndex: isEditorFocused ? -1 : 0, onKeyDown: function (e) {
                                    if (e.target !== e.currentTarget)
                                        return;
                                    if (e.key !== 'Enter' && e.key !== ' ')
                                        return;
                                    if ((jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) || isLengthLocked)
                                        return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (!isEditorFocused)
                                        dataBeforeEditRef.current = __assign({}, data);
                                    setIsEditorFocused(true);
                                    requestAnimationFrame(function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
                                }, children: [_jsx(MathEditor, { data: {
                                            type: 'data',
                                            data: {
                                                text: localText,
                                                color: '#293273',
                                                alignment: 'center',
                                                size: 30,
                                            },
                                        }, color: "#293273", className: "jumpBarMathEditor", idPreffix: "nl_".concat(id, "_label"), index: 0, isTextUpdate: false, cb: handleTextChange, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, placeHolder: " ", viewonly: ((_s = jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) !== null && _s !== void 0 ? _s : false) || isLengthLocked, setActiveMathText: setActiveMathInput, offsetKeypadByEditorHeight: true, ref: function (el) {
                                            mathEditorRef.current = el;
                                        }, blockNewLine: true, 
                                        // Keep the math keypad open and the math-field focused for
                                        // ~500 ms after a math-field is inserted. The JB's
                                        // math-text button is a sibling of MathEditor's editorDiv,
                                        // so the click moves focus off the math-field (and on iPad,
                                        // iOS Safari oscillates focus between mf and editorDiv).
                                        // Without this the keypad disappears and the user has to
                                        // tap the label again to keep typing.
                                        stickyKeypadAfterInsertOnTouch: true }), isEditorFocused && !((_t = jumpBarLock === null || jumpBarLock === void 0 ? void 0 : jumpBarLock.editing) !== null && _t !== void 0 ? _t : false) && !isLengthLocked && (_jsx(ButtonComponent, { type: "imageType5", imgSrc: __assign({}, btnConfig.mathtext.imgSrc), disabled: ((_u = mathEditorRef.current) === null || _u === void 0 ? void 0 : _u.mathFieldInstancesCount()) === 2, defaultCSS: "jumpBarMathBtn".concat(isBelow ? 'Below' : 'Above'), clickActive: true, isActive: activeMathInput === 0, controlActiveFromOutside: true, ariaLabel: t('text.mathText'), title: t('text.mathText'), onMouseDown: function (e) {
                                            e.preventDefault();
                                            addMathText();
                                        }, 
                                        // Keyboard Enter / Space activation: the native button
                                        // dispatches a synthetic click on Enter / Space (see
                                        // ButtonComponent keydown listener); onMouseDown does
                                        // NOT fire from that synthetic click, so without this
                                        // clickHandler keyboard users couldn't open the math
                                        // keypad. insertMath() has a 300 ms internal guard
                                        // (insertMathGuardRef) so mouse users firing both
                                        // mousedown and click still only insert one math-field.
                                        clickHandler: function () { return addMathText(); }, showToolTip: true, direction: isBelow ? 'bottom' : 'top', isWhiteBoard: true, isParentRelative: true }))] })), _jsx("div", { className: "jumpBarArcBracket", style: { clipPath: barClipPath, WebkitClipPath: barClipPath }, children: _jsx("div", { className: "jumpBarArrow ".concat(isForward ? 'jumpBarArrowRight' : 'jumpBarArrowLeft') }) })] })] }), isFocused && (_jsx("button", { type: "button", className: "numberLineKebabBtn", "aria-label": t('text.openOptions') || 'Open options', "aria-haspopup": "menu", 
                // Anchor at the right end of the bar (just past its right
                // edge), vertically centered with the 30 px tall bar. `left`
                // tracks barWidth so the kebab slides as the user resizes
                // the bar. The outer .jumpBarManipulative wrapper carries
                // `transform: scale(jumpBarScale)` which scales the position
                // along with the rest of the bar.
                style: {
                    left: barWidth + 8,
                    top: JUMP_BAR_BAR_HEIGHT / 2,
                    transform: 'translateY(-50%)',
                }, onMouseDown: function (e) { return e.stopPropagation(); }, onTouchStart: function (e) { return e.stopPropagation(); }, onClick: function (e) {
                    e.stopPropagation();
                    if (!selectedTool.includes(id))
                        setSelectedTool([id]);
                    var rect = e.currentTarget.getBoundingClientRect();
                    setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                }, children: _jsx("img", { src: "./assets/images/icons/kebabN.svg", alt: "", "aria-hidden": "true" }) }))] }));
};
export default NumberLineJumpBar;
